import { supabase } from './supabase';

// ============================================================
// Email Notification Service
// ============================================================

// Fetch notification settings from Supabase
export const getNotificationSettings = async () => {
  const { data, error } = await supabase
    .from('notification_settings')
    .select('*')
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching notification settings:', error);
    return null;
  }
  return data;
};

// Save notification settings
export const saveNotificationSettings = async (settings) => {
  // Check if settings exist
  const { data: existing } = await supabase
    .from('notification_settings')
    .select('id')
    .limit(1)
    .single();

  if (existing) {
    const { error } = await supabase
      .from('notification_settings')
      .update({ ...settings, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('notification_settings')
      .insert([settings]);
    if (error) throw error;
  }
};

// Send email via Vercel serverless function
const sendEmail = async (to, subject, html) => {
  try {
    const response = await fetch('/api/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, subject, html }),
    });

    if (!response.ok) {
      const err = await response.json();
      console.error('Email send failed:', err);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Email send error:', error);
    return false;
  }
};

// ============================================================
// Email Templates
// ============================================================

const baseStyle = `
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  max-width: 600px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const headerStyle = `
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  padding: 32px 24px;
  text-align: center;
`;

const footerStyle = `
  background: #f8fafc;
  padding: 20px 24px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  border-top: 1px solid #e2e8f0;
`;

// Template: New alumni request notification (to admin)
const newRequestTemplate = (request) => `
<div style="${baseStyle}">
  <div style="${headerStyle}">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px;">🔔 Pengajuan Alumni Baru</h1>
    <p style="color: #cbd5e1; margin: 8px 0 0; font-size: 14px;">PIMNAS Alumni Network</p>
  </div>
  <div style="padding: 28px 24px;">
    <p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 20px;">
      Hai Admin, ada pengajuan alumni baru yang membutuhkan review:
    </p>
    <div style="background: #f1f5f9; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 8px 0; color: #64748b; width: 130px; vertical-align: top;">Nama</td>
          <td style="padding: 8px 0; color: #1e293b; font-weight: 600;">${request.name || '-'}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Email</td>
          <td style="padding: 8px 0; color: #1e293b;">${request.email || '-'}</td>
        </tr>
        ${request.job ? `<tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Pekerjaan</td>
          <td style="padding: 8px 0; color: #1e293b;">${request.job}${request.institution ? ` · ${request.institution}` : ''}</td>
        </tr>` : ''}
        ${request.tahun_pimnas ? `<tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">PIMNAS ke-</td>
          <td style="padding: 8px 0; color: #1e293b; font-weight: 600;">🏆 ${request.tahun_pimnas}</td>
        </tr>` : ''}
        ${request.domisili ? `<tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Domisili</td>
          <td style="padding: 8px 0; color: #1e293b;">${request.domisili}</td>
        </tr>` : ''}
        ${request.research_interests ? `<tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Minat Riset</td>
          <td style="padding: 8px 0; color: #1e293b;">${request.research_interests}</td>
        </tr>` : ''}
        ${request.message ? `<tr>
          <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Pesan</td>
          <td style="padding: 8px 0; color: #1e293b; font-style: italic;">"${request.message}"</td>
        </tr>` : ''}
      </table>
    </div>
    <div style="text-align: center;">
      <a href="${window.location.origin}/admin/requests" 
         style="display: inline-block; background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%); color: #ffffff; text-decoration: none; padding: 12px 28px; border-radius: 8px; font-weight: 600; font-size: 14px;">
        Review Pengajuan →
      </a>
    </div>
  </div>
  <div style="${footerStyle}">
    <p style="margin: 0;">Email otomatis dari PIMNAS Alumni Network</p>
    <p style="margin: 4px 0 0;">Jangan balas email ini</p>
  </div>
</div>
`;

// Template: Request approved notification (to requester)
const approvedTemplate = (request) => `
<div style="${baseStyle}">
  <div style="${headerStyle}">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px;">✅ Pengajuan Disetujui!</h1>
    <p style="color: #cbd5e1; margin: 8px 0 0; font-size: 14px;">PIMNAS Alumni Network</p>
  </div>
  <div style="padding: 28px 24px;">
    <p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 16px;">
      Hai <strong>${request.name}</strong>,
    </p>
    <p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 20px;">
      Selamat! 🎉 Pengajuan kamu untuk bergabung ke <strong>Direktori Alumni PIMNAS</strong> telah <strong style="color: #16a34a;">disetujui</strong> oleh admin.
    </p>
    <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 16px 20px; margin-bottom: 20px;">
      <p style="color: #166534; font-size: 14px; margin: 0; font-weight: 500;">
        ✨ Profil kamu sekarang sudah tampil di direktori alumni dan dapat diakses oleh seluruh anggota komunitas PIMNAS.
      </p>
    </div>
    <div style="text-align: center;">
      <a href="${window.location.origin}/alumni" 
         style="display: inline-block; background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); color: #ffffff; text-decoration: none; padding: 12px 28px; border-radius: 8px; font-weight: 600; font-size: 14px;">
        Lihat Direktori Alumni →
      </a>
    </div>
  </div>
  <div style="${footerStyle}">
    <p style="margin: 0;">Email otomatis dari PIMNAS Alumni Network</p>
    <p style="margin: 4px 0 0;">Jangan balas email ini</p>
  </div>
</div>
`;

// Template: Request rejected notification (to requester)
const rejectedTemplate = (request) => `
<div style="${baseStyle}">
  <div style="${headerStyle}">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px;">📋 Update Pengajuan</h1>
    <p style="color: #cbd5e1; margin: 8px 0 0; font-size: 14px;">PIMNAS Alumni Network</p>
  </div>
  <div style="padding: 28px 24px;">
    <p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 16px;">
      Hai <strong>${request.name}</strong>,
    </p>
    <p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 20px;">
      Terima kasih telah mengajukan diri ke Direktori Alumni PIMNAS. Sayangnya, pengajuan kamu <strong style="color: #dc2626;">belum dapat disetujui</strong> saat ini.
    </p>
    <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; padding: 16px 20px; margin-bottom: 20px;">
      <p style="color: #991b1b; font-size: 14px; margin: 0; font-weight: 500;">
        Hal ini bisa terjadi karena data yang belum lengkap atau alasan lainnya. Silakan ajukan kembali dengan data yang lebih lengkap.
      </p>
    </div>
    <div style="text-align: center;">
      <a href="${window.location.origin}/ajukan-alumni" 
         style="display: inline-block; background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%); color: #ffffff; text-decoration: none; padding: 12px 28px; border-radius: 8px; font-weight: 600; font-size: 14px;">
        Ajukan Kembali →
      </a>
    </div>
  </div>
  <div style="${footerStyle}">
    <p style="margin: 0;">Email otomatis dari PIMNAS Alumni Network</p>
    <p style="margin: 4px 0 0;">Jangan balas email ini</p>
  </div>
</div>
`;

// ============================================================
// Public API — called from components
// ============================================================

// Notify admin(s) about new alumni request
export const notifyNewRequest = async (request) => {
  try {
    const settings = await getNotificationSettings();
    if (!settings || !settings.notify_new_request || !settings.admin_emails) return;

    const emails = settings.admin_emails.split(',').map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) return;

    await sendEmail(
      emails,
      `[PIMNAS Alumni] Pengajuan Baru dari ${request.name}`,
      newRequestTemplate(request)
    );
  } catch (error) {
    console.error('Failed to send new request notification:', error);
  }
};

// Notify requester that their request was approved
export const notifyRequestApproved = async (request) => {
  try {
    if (!request.email) return;

    const settings = await getNotificationSettings();
    if (!settings || !settings.notify_status_change) return;

    await sendEmail(
      request.email,
      `[PIMNAS Alumni] Pengajuan Kamu Disetujui! 🎉`,
      approvedTemplate(request)
    );
  } catch (error) {
    console.error('Failed to send approval notification:', error);
  }
};

// Notify requester that their request was rejected
export const notifyRequestRejected = async (request) => {
  try {
    if (!request.email) return;

    const settings = await getNotificationSettings();
    if (!settings || !settings.notify_status_change) return;

    await sendEmail(
      request.email,
      `[PIMNAS Alumni] Update Pengajuan Kamu`,
      rejectedTemplate(request)
    );
  } catch (error) {
    console.error('Failed to send rejection notification:', error);
  }
};
